<template>
  <div class="py-2">
    <b-overlay :show="isLoading">
      <vams-collapse title="Uptake Insights" :visible="defaultShowInsights ? defaultShowInsights : visible">
        <b-table
          :items="displayedInsights"
          :fields="fields"
          table-variant="blue"
          thead-tr-class="text-small"
          tbody-tr-class="bg-half-alpha"
        >
          <template #head(selected)>
            <div class="text-center">
              <input v-model="selectAll" type="checkbox" />
            </div>
          </template>

          <template #cell(selected)="{ item }">
            <div class="text-center">
              <input
                v-show="!item.isAssigned && !item.isAssignedOther"
                v-model="item.isSelected"
                type="checkbox"
                @click="select(item)"
              />
              <font-awesome-icon
                v-show="item.isAssigned"
                v-b-tooltip.hover
                icon="check"
                class="text-success"
                :title="`Assigned to this SO: ${item.serviceOrderId}, Op#: ${item.operationId}`"
              />
            </div>
          </template>
          <template #cell(severity)="{ item }">
            <div :style="formatSeverity(item.insightSeverity)">{{ item.insightSeverity }}</div>
          </template>
          <template #cell(insightTitle)="{ item }">
            {{ item.insightTitle }} -
            {{ item.showMoreDescription ? item.insightDescription : formatText(item.insightDescription) }}
            <b-link v-if="!item.showMoreDescription" @click="showMore(item, true, 'Description')">More</b-link>
            <b-link v-else @click="showMore(item, false, 'Description')">Less</b-link>
          </template>
          <template #cell(jobId)="{ item }">
            <div v-if="item.isAssigned">{{ item.excedeJobId }} - {{ item.excedeJobDescription }}</div>
            <v-select
              v-else
              :value="item.excedeJob"
              label="description"
              placeholder="Select Operation"
              :class="{ 'is-invalid': hasValidationError(item) }"
              :options="nonScheduledOperationsList"
              :clearable="true"
              :filterable="true"
              :filter-by="operationDropdownSearchFilter"
              @input="handleOperationSelection(item, $event)"
            >
              <template #selected-option="operation">
                <span v-if="operation && operation.id" class="text-uppercase">
                  {{ operation.id }} - {{ operation.description }}
                </span>
              </template>
              <template #option="operation">
                <span class="text-uppercase">{{ operation.id }} - {{ operation.description }}</span>
              </template>
            </v-select>
          </template>
          <template #cell(recommendedAction)="{ item }">
            <div class="mt-3">
              Recommended Action
              <span
                v-b-tooltip.hover.html="{
                  title: formatRecommendedActionTooltip(item.recommendedAction),
                  customClass: 'recommended-action-tooltip'
                }"
                class="d-inline-block"
              >
                <font-awesome-icon
                  icon="external-link-alt"
                  size="lg"
                  class="mx-1 text-primary"
                  display="inline-block"
                />
              </span>
            </div>
          </template>
          <template #cell(insightStartTs)="{ item }">
            {{ item.insightStartTs | date }}
          </template>
        </b-table>
      </vams-collapse>
    </b-overlay>
  </div>
</template>

<script>
import CollapseComponent from '@/shared/components/ui/CollapseComponent';

import { mapGetters, mapMutations } from 'vuex';
import { ServiceOrderGetters } from '@/shared/store/service-order/types';
import { UnitGetters } from '@/shared/store/unit/types';
import { UptakeInsightsGetters, UptakeInsightsMutations } from '@/shared/store/uptake-insights/types';
import { UserGetters } from '@/shared/store/user/types';
import { LookupGetters } from '@/shared/store/lookup/types';
import vSelect from 'vue-select';
import { helpers } from 'vuelidate/lib/validators';

function requiredIfSelected(value, parentVm) {
  return parentVm.isSelected ? helpers.req(value) : true;
}
export default {
  name: 'ServiceOrderAddInsights',
  components: {
    'vams-collapse': CollapseComponent,
    'v-select': vSelect
  },
  props: {
    visible: Boolean()
  },
  data: function () {
    return {
      fields: [
        { key: 'selected', label: '', sortable: false, tdClass: 'align-middle', thStyle: 'width: 50px' },
        { key: 'insightTitle', label: 'Insight', sortable: true, tdClass: 'align-middle', thStyle: 'width: 475px' },
        { key: 'severity', label: 'Severity', sortable: true, tdClass: 'align-middle' },
        { key: 'jobId', label: 'Job ID', sortable: true, tdClass: 'align-middle', thStyle: 'width: 475px' },
        { key: 'recommendedAction', label: 'Action', sortable: false },
        { key: 'insightStartTs', label: 'Date Created', sortable: true, tdClass: 'align-middle' }
      ],
      isLoading: false,
      defaultShowInsights: false
    };
  },
  validations() {
    return {
      unitInsights: {
        $each: {
          excedeJob: {
            requiredIfSelected
          }
        }
      }
    };
  },
  computed: {
    ...mapGetters({
      unitInsights: UptakeInsightsGetters.GET_UNIT_UPTAKE_INSIGHTS,
      unit: UnitGetters.GET_UNIT,
      serviceOrderId: ServiceOrderGetters.GET_SERVICE_ORDER_ID,
      user: UserGetters.GET_USER_PROFILE,
      nonScheduledOperationsList: LookupGetters.GET_NON_SCHEDULED_OPERATIONS_LIST,
      serviceOrder: ServiceOrderGetters.GET_SERVICE_ORDER,
      showInsights: UptakeInsightsGetters.GET_SHOW_INSIGHTS
    }),
    displayedInsights() {
      return this.unitInsights.filter(ins => {
        return !ins.isAssignedOther && !ins.isDuplicate;
      });
    },
    selectAll: {
      get() {
        return (
          this.unitInsights.length ==
          this.unitInsights.filter(ins => ins.isSelected || ins.isAssigned || ins.isAssignedOther || ins.isDuplicate)
            .length
        );
      },
      set(value) {
        this.unitInsights.forEach(ins => {
          if (!ins.isAssigned && !ins.isAssignedOther && !ins.isDuplicate) {
            this.setJobProperty(ins.uptakeInsightId, 'isSelected', value);
          }
        });
      }
    }
  },
  watch: {
    unitInsights: {
      handler() {
        this.unitInsights.forEach(ins => {
          if (ins.serviceOrderId && ins.serviceOrderId.substring(0, 10) === this.serviceOrderId.substring(0, 10)) {
            this.setJobProperty(ins.uptakeInsightId, 'isAssigned', true);
          }
          if (ins.serviceOrderId && ins.serviceOrderId.substring(0, 10) != this.serviceOrderId.substring(0, 10)) {
            this.setJobProperty(ins.uptakeInsightId, 'isAssignedOther', true);
          }
          if (ins.serviceOrderId == null && ins.insightActions[0].actionState == 'ACCEPTED') {
            this.setJobProperty(ins.uptakeInsightId, 'isDuplicate', true);
          }
        });
      },
      immediate: true
    },
    showInsights: {
      handler() {
        this.defaultShowInsights = this.showInsights;
      },
      immediate: true
    }
  },
  methods: {
    ...mapMutations([UptakeInsightsMutations.SET_JOB_PROPERTY]),
    isAssigned(job) {
      const jobIds = Object.values(this.selectedJobs).map(job => job.id);
      return jobIds.includes(job.id);
    },
    hasValidationError(item) {
      const index = this.unitInsights.findIndex(ins => ins.uptakeInsightId === item.uptakeInsightId);
      if (index === -1) return false;
      return this.$v.unitInsights.$each[index].excedeJob.$error;
    },
    validateForSubmission() {
      this.$v.unitInsights.$touch();
      return !this.$v.unitInsights.$invalid;
    },
    select(item) {
      var jobToSelect = this.unitInsights.find(i => i.uptakeInsightId === item.uptakeInsightId);
      this.setJobProperty(jobToSelect.uptakeInsightId, 'isSelected', !jobToSelect.isSelected);
      if (jobToSelect.excedeJob?.id) {
        this.setJobProperty(jobToSelect.uptakeInsightId, 'excedeJob', null);
      }
    },
    handleOperationSelection(item, selectedOperation) {
      if (!item.isSelected) {
        this.setJobProperty(item.uptakeInsightId, 'isSelected', true);
      }
      this.setJobProperty(item.uptakeInsightId, 'excedeJob', selectedOperation);
      this.setJobProperty(item.uptakeInsightId, 'excedeJobId', selectedOperation.id);
    },
    setJobProperty(uptakeInsightId, property, value) {
      this[UptakeInsightsMutations.SET_JOB_PROPERTY]({ uptakeInsightId, property, value });
    },

    operationDropdownSearchFilter(option, label, search) {
      return this.dropdownSearchFilter(option.id, option.description, search);
    },
    dropdownSearchFilter(id, description, search) {
      search = search.toLowerCase();
      id = id.toLowerCase();
      description = description.toLowerCase();

      return id.indexOf(search) > -1 || description.indexOf(search) > -1;
    },
    formatSeverity(severity) {
      switch (severity.toLowerCase()) {
        case 'critical':
          return 'color: red;';
        case 'catastrophic':
          return 'color: red;';
        case 'marginal':
          return 'color: orange;';
        case 'negligible':
          return 'color: green; ';
        default:
          return '';
      }
    },
    formatText(item) {
      var length = 130;
      var clamp = clamp || '...';
      return item?.length > length ? item.slice(0, length) + clamp : item;
    },
    showMore(item, value, type) {
      if (type === 'Description') {
        this.setJobProperty(item.uptakeInsightId, 'showMoreDescription', value);
      } else if (type === 'Action') {
        this.setJobProperty(item.uptakeInsightId, 'showMoreAction', value);
      }
    },
    formatRecommendedActionTooltip(text) {
      if (!text) return '';
      const safe = this.escapeHtml(String(text).trim());
      // Insert a line break before any "number + dot + space" that is preceded by whitespace.
      return safe.replace(/\s(?=\d+\.\s)/g, '<br>');
    },
    escapeHtml(value) {
      return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }
};
</script>
<style>
.recommended-action-tooltip .tooltip-inner {
  max-width: 800px;
  width: 800px;
  white-space: pre-line;
  text-align: left;
}
</style>
